// Globals
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableRowSelection } from 'antd/es/table/interface';

// Models
import { IFaqBase } from '@/models/faqBase.model';
import { IPagination } from '@/models/pagination.model';

// Components
import { Pagination, Table } from '@/ui';
import EditFaqModal from '../EditFaqModal/EditFaqModal';
import DeleteFaqConfirmModal from '../DeleteFaqConfirmModal/DeleteFaqConfirmModal';

// Helpers
import { getFaqColumns } from '../../helpers/getFaqColumns';
import { TFaqFormValues } from '../../helpers';
import { TFaqSearchFilters } from '../../hooks';

type TFaqTableProps<T extends IFaqBase> = {
  data: T[] | null;
  isLoading?: boolean;
  pagination?: IPagination | null;
  onPageChange: (page: number) => void;
  filters?: TFaqSearchFilters;
  onFilterChange?: (field: keyof TFaqSearchFilters, value: string) => void;
  onEditFaq: (id: string, values: TFaqFormValues) => void | Promise<void>;
  onDeleteFaq: (id: string) => void | Promise<void>;
  rowSelection?: TableRowSelection<T>;
};

const FaqTable = <T extends IFaqBase>({
  data,
  isLoading,
  pagination,
  onPageChange,
  filters,
  onFilterChange,
  onEditFaq,
  onDeleteFaq,
  rowSelection,
}: TFaqTableProps<T>) => {
  const { t } = useTranslation();
  const [editingFaq, setEditingFaq] = useState<T | null>(null);
  const [deletingFaq, setDeletingFaq] = useState<T | null>(null);

  const handleEditSubmit = async (values: TFaqFormValues) => {
    if (!editingFaq) return;
    await onEditFaq(editingFaq.faqId, values);
    setEditingFaq(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingFaq) return;
    await onDeleteFaq(deletingFaq.faqId);
    setDeletingFaq(null);
  };

  const columns = getFaqColumns<T>({
    t,
    filters,
    onFilterChange,
    onEdit: setEditingFaq,
    onDelete: setDeletingFaq,
  });

  return (
    <>
      <Table
        rowSelection={rowSelection}
        loading={isLoading}
        columns={columns}
        rowKey="faqId"
        dataSource={data ?? []}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      <Pagination
        hideOnSinglePage
        total={pagination?.total}
        current={pagination?.page}
        pageSize={pagination?.pageSize}
        align="center"
        onChange={onPageChange}
      />
      <EditFaqModal
        isOpen={!!editingFaq}
        onClose={() => setEditingFaq(null)}
        faq={editingFaq}
        onSubmit={handleEditSubmit}
        isLoading={isLoading}
      />
      <DeleteFaqConfirmModal
        isOpen={!!deletingFaq}
        onClose={() => setDeletingFaq(null)}
        onConfirm={handleConfirmDelete}
        isLoading={isLoading}
        title={t('delete_faq_confirm')}
        btnTitle={t('btn_faq_delete')}
        description={t('delete_description')}
      />
    </>
  );
};

export default FaqTable;
